import { useEffect, useRef } from 'react'
import {
  FieldValues,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form'
import { merge } from 'lodash'
import { FormRuleType, setFormBehaviorState } from '@app-store'
import { store } from '../utils/agent.store'
import { EMPTY_FORM_BEHAVIOR_STATE } from './formBehavior.utils'
import { clearRuleCache } from './formBehaviorRuleCache.registry'
import {
  FormBehaviorTriggerSource,
  registerFormBehaviorEvaluator,
  unregisterFormBehaviorEvaluator,
} from './formBehaviorTrigger.registry'
import { handleFormBehaviorRule } from './handlers/formBehaviorRule.handler'
import { syncContextualLookupFields } from './syncContextualLookupFields'
import { FormBehaviorState } from '../types/formBehavior.types'

const clearFormFields = (
  setValue: UseFormSetValue<FieldValues>,
  keyNames: string[]
) => {
  keyNames.forEach((keyName) => {
    setValue(keyName, '' as never, { shouldDirty: true })
    setValue(`lookupLabels.${keyName}` as never, '' as never)
  })
}

const cloneFormBehaviorState = (
  state: FormBehaviorState
): FormBehaviorState => ({
  requiredOverrides: { ...state.requiredOverrides },
  disabledOverrides: { ...state.disabledOverrides },
  hiddenOverrides: { ...state.hiddenOverrides },
  lookupAllowedIds: { ...state.lookupAllowedIds },
  lookupAllowedLabels: { ...state.lookupAllowedLabels },
  categoryAllowedIds: { ...state.categoryAllowedIds },
  categoryAllowedLabels: { ...state.categoryAllowedLabels },
  minDateOverrides: { ...state.minDateOverrides },
  maxDateOverrides: { ...state.maxDateOverrides },
})

const getRulesForEval = (
  rules: FormRuleType[],
  source: FormBehaviorTriggerSource,
  changedKeyName?: string
): FormRuleType[] => {
  if (source === 'init' || source === 'hydrate' || !changedKeyName) {
    return rules
  }
  return rules.filter((rule) =>
    (rule.when as { watch_key_names?: string[] })?.watch_key_names?.includes(
      changedKeyName
    )
  )
}

export const useFormBehaviorEngine = (
  layoutUid: string,
  rules: FormRuleType[],
  setValue: UseFormSetValue<FieldValues>,
  getValues: UseFormGetValues<FieldValues>,
  reset: UseFormReset<FieldValues>,
  dispatch: typeof store.dispatch,
  formInstanceId?: string,
  formUniqueId?: string,
  entityName?: string
): void => {
  const evalGenerationRef = useRef(0)

  useEffect(() => {
    if (!rules.length) return

    const evaluate = async (
      source: FormBehaviorTriggerSource,
      changedKeyName?: string
    ) => {
      const generation = ++evalGenerationRef.current
      const rulesToRun = getRulesForEval(rules, source, changedKeyName)
      if (!rulesToRun.length) return

      if (source === 'change' && changedKeyName) {
        for (const rule of rulesToRun) {
          const watchNames =
            (rule.when as { watch_key_names?: string[] })?.watch_key_names ?? []
          const clearKeyNames =
            (rule.then as { clear_key_names?: string[] })?.clear_key_names ?? []

          if (watchNames.includes(changedKeyName) && clearKeyNames.length) {
            clearFormFields(setValue, clearKeyNames)
          }
        }
      }

      const currentEntry =
        source === 'init'
          ? EMPTY_FORM_BEHAVIOR_STATE
          : store.getState().formBehavior.byLayoutUid[layoutUid] ??
            EMPTY_FORM_BEHAVIOR_STATE

      const nextState = cloneFormBehaviorState(currentEntry)
      const mergedFieldValues: Record<string, unknown | null> = {}

      for (const rule of rulesToRun) {
        const result = await handleFormBehaviorRule(
          layoutUid,
          rule,
          getValues,
          { skipSetValue: source === 'hydrate', formInstanceId }
        )
        if (generation !== evalGenerationRef.current && source !== 'init')
          return

        if (result) {
          Object.assign(nextState.requiredOverrides, result.requiredOverrides)
          Object.assign(nextState.disabledOverrides, result.disabledOverrides)
          Object.assign(nextState.hiddenOverrides, result.hiddenOverrides)
          Object.assign(nextState.lookupAllowedIds, result.lookupAllowedIds)
          Object.assign(
            nextState.lookupAllowedLabels,
            result.lookupAllowedLabels
          )
          Object.assign(nextState.categoryAllowedIds, result.categoryAllowedIds)
          Object.assign(
            nextState.categoryAllowedLabels,
            result.categoryAllowedLabels
          )
          Object.assign(nextState.minDateOverrides, result.minDateOverrides)
          Object.assign(nextState.maxDateOverrides, result.maxDateOverrides)
          if (result.fieldValues) {
            Object.assign(mergedFieldValues, result.fieldValues)
          }
        }
      }

      if (generation !== evalGenerationRef.current && source !== 'init') return

      dispatch(setFormBehaviorState({ layoutUid, state: nextState }))

      if (!Object.keys(mergedFieldValues).length) return

      if (source === 'init') {
        reset(merge(getValues(), mergedFieldValues), { keepDirtyValues: true })
        evaluate('hydrate')
        if (formUniqueId && entityName) {
          syncContextualLookupFields(
            dispatch,
            formUniqueId,
            entityName,
            mergedFieldValues
          )
        }
        return
      }

      for (const [key, value] of Object.entries(mergedFieldValues)) {
        setValue(key, (value ?? '') as never, { shouldDirty: true })
      }
    }

    registerFormBehaviorEvaluator(layoutUid, evaluate)

    return () => {
      unregisterFormBehaviorEvaluator(layoutUid)
      clearRuleCache(layoutUid)
    }
  }, [
    layoutUid,
    rules,
    getValues,
    setValue,
    reset,
    dispatch,
    formInstanceId,
    formUniqueId,
    entityName,
  ])
}
